import { Utils } from '.'
import { Objects } from '../object-definitions'
import { assertUnreachable } from './others'

const DIMENSIONS: Objects.Product.Code.DimensionsMap = {
    BROCHURE: {
        heightInches: 11,
        widthInches: 17,
    },
    POSTCARD: {
        heightInches: 6,
        widthInches: 9,
    },
    'GREETING-CARD': {
        widthInches: 7.25,
        heightInches: 5.25,
    },
    LETTER: {
        heightInches: 11,
        widthInches: 8.5,
    },
    FLYER: {
        heightInches: 11,
        widthInches: 8.5,
    },
    'BROCHURE:PRINT-AND-SHIP-ONLY': {
        heightInches: 11,
        widthInches: 17,
    },
    'BUSINESS-CARD:HORIZONTAL': {
        heightInches: 2,
        widthInches: 3.5,
    },
    'BUSINESS-CARD:VERTICAL': {
        heightInches: 3.5,
        widthInches: 2,
    },
    'DOOR-HANGER': {
        heightInches: 11,
        widthInches: 4.25,
    },
    'TRI-FOLD-BROCHURE': {
        heightInches: 8.5,
        widthInches: 11,
    },
    'SIGN:HORIZONTAL': {
        heightInches: 18,
        widthInches: 24,
    },
    'SIGN:RIDER': {
        heightInches: 6,
        widthInches: 24,
    },
    'SIGN:VERTICAL': {
        heightInches: 36,
        widthInches: 24,
    },
    'SIGN:YARD': {
        heightInches: 18,
        widthInches: 24,
    },
    'GREETING-CARD:HORIZONTAL': {
        heightInches: 5.5,
        widthInches: 8.5,
    },
    'GREETING-CARD:VERTICAL': {
        heightInches: 8.5,
        widthInches: 5.5,
    },
}

const allProductCodes: Objects.Product.Code.All = [
    'BROCHURE',
    'FLYER',
    'GREETING-CARD',
    'LETTER',
    'POSTCARD',
    'BROCHURE:PRINT-AND-SHIP-ONLY',
    'BUSINESS-CARD:VERTICAL',
    'BUSINESS-CARD:HORIZONTAL',
    'DOOR-HANGER',
    'TRI-FOLD-BROCHURE',
    'SIGN:RIDER',
    'SIGN:YARD',
    'SIGN:VERTICAL',
    'SIGN:HORIZONTAL',
    'GREETING-CARD:HORIZONTAL',
    'GREETING-CARD:VERTICAL',
]

const allProductTypes: Objects.Product.Type.All = [
    'POSTCARD',
    'BROCHURE',
    'GREETING-CARD',
    'LETTER',
    'FLYER',
    'BUSINESS-CARD',
    'DOOR-HANGER',
    'TRI-FOLD-BROCHURE',
    'SIGN',
]

const allProductTypeVariants: Objects.Product.TypeVariant.All = ['PRINT-AND-SHIP-ONLY', 'VERTICAL', 'HORIZONTAL', 'RIDER', 'YARD']
export namespace Product {
    export function isValidProductType(productType: Objects.Product.Type.Any) {
        return allProductTypes.includes(productType)
    }

    export function isValidProductTypeVariant(productTypeVariant: Objects.Product.TypeVariant.Any) {
        return allProductTypeVariants.includes(productTypeVariant)
    }

    export function getProductCodeFromDocument(document: Objects.Chili.Document): Objects.Product.Code.Any | undefined {
        const LETTER_BARCODE_01_NAME: Objects.MailingOrder.LetterBarcodeFieldHeader.Page01 = '<<LetterBarcode01>>'

        if (!document || !document.document) {
            throw new Error('Document not found')
        }
        const letterBarcode01Exists =
            document.document?.variables[0]?.item?.find((variable) => {
                return variable.$.dataType === 'string' && variable.$.name === LETTER_BARCODE_01_NAME
            }) !== undefined

        const { heightInches, widthInches } = Utils.Chili.getDocumentDimensions(document)
        const matchingProductType = allProductCodes.find((productCode) => {
            switch (productCode) {
                case 'BUSINESS-CARD:HORIZONTAL':
                case 'BUSINESS-CARD:VERTICAL':
                case 'DOOR-HANGER':
                case 'GREETING-CARD':
                case 'GREETING-CARD:HORIZONTAL':
                case 'GREETING-CARD:VERTICAL':
                case 'POSTCARD':
                // TODO(products) Sign Yard and Sign Horizonal have the same size. Need to differentiate
                case 'SIGN:HORIZONTAL':
                case 'SIGN:RIDER':
                case 'SIGN:VERTICAL':
                case 'SIGN:YARD':
                case 'TRI-FOLD-BROCHURE':
                    return widthInches === DIMENSIONS[productCode].widthInches && heightInches === DIMENSIONS[productCode].heightInches
                case 'BROCHURE':
                    return (
                        widthInches === DIMENSIONS[productCode].widthInches &&
                        heightInches === DIMENSIONS[productCode].heightInches &&
                        !((document.document.pages[0].item[0].frames?.[0].item ?? []).filter((frame) => frame?.$.layerName === 'Front').length < 3)
                    )
                case 'BROCHURE:PRINT-AND-SHIP-ONLY':
                    return (
                        widthInches === DIMENSIONS[productCode].widthInches &&
                        heightInches === DIMENSIONS[productCode].heightInches &&
                        (document.document.pages[0].item[0].frames?.[0].item ?? []).filter((frame) => frame?.$.layerName === 'Front').length < 3
                    )
                case 'LETTER':
                    return widthInches === DIMENSIONS[productCode].widthInches && heightInches === DIMENSIONS[productCode].heightInches && letterBarcode01Exists
                case 'FLYER':
                    return (
                        widthInches === DIMENSIONS[productCode].widthInches && heightInches === DIMENSIONS[productCode].heightInches && !letterBarcode01Exists
                    )
            }
            return assertUnreachable(productCode)
        })
        return matchingProductType
    }

    interface ProductOption {
        type: Objects.Product.Type.Any
        variant: Objects.Product.TypeVariant.Any | undefined
    }
    export function getProductOptionsFromDocument(document: Objects.Chili.Document, opts: { strict: boolean }): ProductOption[] | undefined {
        const LETTER_BARCODE_01_NAME: Objects.MailingOrder.LetterBarcodeFieldHeader.Page01 = '<<LetterBarcode01>>'
        if (!document || !document.document) {
            throw new Error('Document not found')
        }

        const letterBarcode01Exists =
            document.document?.variables[0]?.item?.find((variable) => {
                return variable.$.dataType === 'string' && variable.$.name === LETTER_BARCODE_01_NAME
            }) !== undefined
        const { heightInches, widthInches } = Utils.Chili.getDocumentDimensions(document)
        const matchingProductType = allProductTypes.filter((product) => {
            switch (product) {
                case 'POSTCARD':
                    return widthInches === DIMENSIONS['POSTCARD'].widthInches && heightInches === DIMENSIONS['POSTCARD'].heightInches
                case 'BROCHURE':
                    return widthInches === DIMENSIONS['BROCHURE'].widthInches && heightInches === DIMENSIONS['BROCHURE'].heightInches
                case 'GREETING-CARD':
                    return (
                        (widthInches === DIMENSIONS['GREETING-CARD'].widthInches && heightInches === DIMENSIONS['GREETING-CARD'].heightInches) ||
                        (widthInches === DIMENSIONS['GREETING-CARD:HORIZONTAL'].widthInches &&
                            heightInches === DIMENSIONS['GREETING-CARD:HORIZONTAL'].heightInches) ||
                        (widthInches === DIMENSIONS['GREETING-CARD:VERTICAL'].widthInches && heightInches === DIMENSIONS['GREETING-CARD:VERTICAL'].heightInches)
                    )
                case 'LETTER': {
                    const matchesDimensions = widthInches === DIMENSIONS['LETTER'].widthInches && heightInches === DIMENSIONS['LETTER'].heightInches
                    if (opts.strict) {
                        return matchesDimensions && letterBarcode01Exists
                    } else {
                        return matchesDimensions
                    }
                }
                case 'FLYER':
                    const matchesDimensions = widthInches === DIMENSIONS['LETTER'].widthInches && heightInches === DIMENSIONS['LETTER'].heightInches
                    if (opts.strict) {
                        return matchesDimensions && !letterBarcode01Exists
                    } else {
                        return matchesDimensions
                    }
                case 'BUSINESS-CARD':
                    return (
                        (widthInches === DIMENSIONS['BUSINESS-CARD:HORIZONTAL'].widthInches &&
                            heightInches === DIMENSIONS['BUSINESS-CARD:HORIZONTAL'].heightInches) ||
                        (widthInches === DIMENSIONS['BUSINESS-CARD:VERTICAL'].widthInches && heightInches === DIMENSIONS['BUSINESS-CARD:VERTICAL'].heightInches)
                    )
                case 'DOOR-HANGER':
                    return widthInches === DIMENSIONS['DOOR-HANGER'].widthInches && heightInches === DIMENSIONS['DOOR-HANGER'].heightInches
                case 'TRI-FOLD-BROCHURE':
                    return widthInches === DIMENSIONS['TRI-FOLD-BROCHURE'].widthInches && heightInches === DIMENSIONS['TRI-FOLD-BROCHURE'].heightInches
                case 'SIGN':
                    return (
                        (widthInches === DIMENSIONS['SIGN:HORIZONTAL'].widthInches && heightInches === DIMENSIONS['SIGN:HORIZONTAL'].heightInches) ||
                        (widthInches === DIMENSIONS['SIGN:RIDER'].widthInches && heightInches === DIMENSIONS['SIGN:RIDER'].heightInches) ||
                        (widthInches === DIMENSIONS['SIGN:VERTICAL'].widthInches && heightInches === DIMENSIONS['SIGN:VERTICAL'].heightInches) ||
                        (widthInches === DIMENSIONS['SIGN:YARD'].widthInches && heightInches === DIMENSIONS['SIGN:YARD'].heightInches)
                    )
            }
            return assertUnreachable(product)
        })

        if (matchingProductType.length === 0) {
            return undefined
        }

        return matchingProductType.map<ProductOption>((type) => {
            switch (type) {
                case 'BROCHURE': {
                    let variant: ProductOption['variant'] = undefined
                    if ((document.document.pages[0].item[0].frames?.[0].item ?? []).filter((frame) => frame?.$.layerName === 'Front').length < 3) {
                        variant = 'PRINT-AND-SHIP-ONLY'
                    }

                    return {
                        type,
                        variant,
                    }
                }
                case 'BUSINESS-CARD': {
                    let variant: ProductOption['variant'] = 'HORIZONTAL'
                    if (widthInches === 2) {
                        variant = 'VERTICAL'
                    }
                    return {
                        type,
                        variant,
                    }
                }
                case 'DOOR-HANGER':
                    return {
                        type,
                        variant: undefined,
                    }
                case 'GREETING-CARD':
                    let variant: ProductOption['variant'] = undefined
                    if (
                        widthInches === DIMENSIONS['GREETING-CARD:HORIZONTAL'].widthInches &&
                        heightInches === DIMENSIONS['GREETING-CARD:HORIZONTAL'].heightInches
                    ) {
                        variant = 'HORIZONTAL'
                    } else if (
                        widthInches === DIMENSIONS['GREETING-CARD:VERTICAL'].widthInches &&
                        heightInches === DIMENSIONS['GREETING-CARD:VERTICAL'].heightInches
                    ) {
                        variant = 'VERTICAL'
                    }

                    return {
                        type,
                        variant,
                    }
                case 'FLYER':
                    return {
                        type,
                        variant: undefined,
                    }
                case 'LETTER':
                    return {
                        type,
                        variant: undefined,
                    }
                case 'POSTCARD':
                    return {
                        type,
                        variant: undefined,
                    }
                case 'SIGN': {
                    let variant: ProductOption['variant'] = 'HORIZONTAL'
                    if (heightInches === 6) {
                        variant = 'RIDER'
                    } else if (heightInches === 18) {
                        variant = 'YARD'
                    } else if (heightInches === 36) {
                        variant = 'VERTICAL'
                    }
                    return {
                        type,
                        variant,
                    }
                }
                case 'TRI-FOLD-BROCHURE':
                    return {
                        type,
                        variant: undefined,
                    }
            }
            assertUnreachable(type)
        })
    }

    export function getProductTypeFromDocument(document: Objects.Chili.Document): Objects.Product.Type.Any | undefined {
        const LETTER_BARCODE_01_NAME: Objects.MailingOrder.LetterBarcodeFieldHeader.Page01 = '<<LetterBarcode01>>'
        if (!document || !document.document) {
            throw new Error('Document not found')
        }

        const letterBarcode01Exists =
            document.document?.variables[0]?.item?.find((variable) => {
                return variable.$.dataType === 'string' && variable.$.name === LETTER_BARCODE_01_NAME
            }) !== undefined
        const { heightInches, widthInches } = Utils.Chili.getDocumentDimensions(document)

        const matchingProductType = allProductTypes.find((product) => {
            switch (product) {
                case 'BROCHURE':
                case 'DOOR-HANGER':
                case 'POSTCARD':
                case 'TRI-FOLD-BROCHURE':
                    return widthInches === DIMENSIONS[product].widthInches && heightInches === DIMENSIONS[product].heightInches
                case 'GREETING-CARD':
                    return (
                        (widthInches === DIMENSIONS['GREETING-CARD'].widthInches && heightInches === DIMENSIONS['GREETING-CARD'].heightInches) ||
                        (widthInches === DIMENSIONS['GREETING-CARD:HORIZONTAL'].widthInches &&
                            heightInches === DIMENSIONS['GREETING-CARD:HORIZONTAL'].heightInches) ||
                        (widthInches === DIMENSIONS['GREETING-CARD:VERTICAL'].widthInches && heightInches === DIMENSIONS['GREETING-CARD:VERTICAL'].heightInches)
                    )
                case 'LETTER':
                    return widthInches === DIMENSIONS['LETTER'].widthInches && heightInches === DIMENSIONS['LETTER'].heightInches && letterBarcode01Exists
                case 'FLYER':
                    return widthInches === DIMENSIONS['LETTER'].widthInches && heightInches === DIMENSIONS['LETTER'].heightInches && !letterBarcode01Exists
                case 'BUSINESS-CARD':
                    return (
                        (widthInches === DIMENSIONS['BUSINESS-CARD:HORIZONTAL'].widthInches &&
                            heightInches === DIMENSIONS['BUSINESS-CARD:HORIZONTAL'].heightInches) ||
                        (widthInches === DIMENSIONS['BUSINESS-CARD:VERTICAL'].widthInches && heightInches === DIMENSIONS['BUSINESS-CARD:VERTICAL'].heightInches)
                    )
                case 'SIGN':
                    return (
                        (widthInches === DIMENSIONS['SIGN:HORIZONTAL'].widthInches && heightInches === DIMENSIONS['SIGN:HORIZONTAL'].heightInches) ||
                        (widthInches === DIMENSIONS['SIGN:RIDER'].widthInches && heightInches === DIMENSIONS['SIGN:RIDER'].heightInches) ||
                        (widthInches === DIMENSIONS['SIGN:VERTICAL'].widthInches && heightInches === DIMENSIONS['SIGN:VERTICAL'].heightInches) ||
                        (widthInches === DIMENSIONS['SIGN:YARD'].widthInches && heightInches === DIMENSIONS['SIGN:YARD'].heightInches)
                    )
            }
            return assertUnreachable(product)
        })
        return matchingProductType
    }

    export function getProductTypeVariantFromDocument(document: Objects.Chili.Document): Objects.Product.TypeVariant.Any | undefined {
        if (!document || !document.document) {
            throw new Error('Document not found')
        }

        const productType = getProductTypeFromDocument(document)
        const { heightInches, widthInches } = Utils.Chili.getDocumentDimensions(document)

        // TODO(products) no TS warning when a new product type or variant is added
        switch (productType) {
            case 'BROCHURE':
                if ((document.document.pages[0].item[0].frames?.[0].item ?? []).filter((frame) => frame?.$.layerName === 'Front').length < 3) {
                    return 'PRINT-AND-SHIP-ONLY'
                }
                return undefined
            case 'GREETING-CARD':
                if (
                    widthInches === DIMENSIONS['GREETING-CARD:HORIZONTAL'].widthInches &&
                    heightInches === DIMENSIONS['GREETING-CARD:HORIZONTAL'].heightInches
                ) {
                    return 'HORIZONTAL'
                } else if (
                    widthInches === DIMENSIONS['GREETING-CARD:VERTICAL'].widthInches &&
                    heightInches === DIMENSIONS['GREETING-CARD:VERTICAL'].heightInches
                ) {
                    return 'VERTICAL'
                }
                return undefined
            case 'POSTCARD':
                return undefined
            case 'LETTER':
                return undefined
            case 'FLYER':
                return undefined
            case 'BUSINESS-CARD':
                if (Utils.Chili.getDocumentDimensions(document).widthInches === 2) {
                    return 'VERTICAL'
                }
                return 'HORIZONTAL'
            case 'DOOR-HANGER':
                return undefined
            case 'TRI-FOLD-BROCHURE':
                return undefined
            case 'SIGN':
                if (widthInches === 24 && heightInches === 6) {
                    return 'RIDER'
                }
                if (widthInches === 24 && heightInches === 18) {
                    return 'YARD'
                }
                if (heightInches === 36 && widthInches === 24) {
                    return 'VERTICAL'
                }
                return 'HORIZONTAL'
            case undefined:
                return undefined
        }

        return assertUnreachable(productType)
    }

    export function getAvailableProductVariantsForProduct(productType: Objects.Product.Type.Any | undefined): Objects.Product.AvailableVariants.Any {
        // TODO(products) no TS warning when a new product type or variant is added
        switch (productType) {
            case 'BROCHURE':
                return ['PRINT-AND-SHIP-ONLY']
            case 'GREETING-CARD':
                return ['HORIZONTAL', 'VERTICAL']
            case 'POSTCARD':
                return []
            case 'LETTER':
                return []
            case 'FLYER':
                return []
            case 'BUSINESS-CARD':
                return ['VERTICAL', 'HORIZONTAL']
            case 'DOOR-HANGER':
                return []
            case 'TRI-FOLD-BROCHURE':
                return []
            case 'SIGN':
                return ['RIDER', 'YARD', 'VERTICAL', 'HORIZONTAL']
            case undefined:
                return []
        }

        assertUnreachable(productType)
    }

    export function isValidDirectMailCode(productCode: Objects.Product.Code.Any | undefined): productCode is Objects.Product.Code.Fulfillment.DirectMail {
        if (!productCode) {
            return false
        }

        const validDirectMailProductTypeMap: Record<Objects.Product.Code.Any, boolean> = {
            POSTCARD: true,
            BROCHURE: true,
            FLYER: false,
            'GREETING-CARD': false,
            LETTER: true,
            'BROCHURE:PRINT-AND-SHIP-ONLY': false,
            'BUSINESS-CARD:HORIZONTAL': false,
            'BUSINESS-CARD:VERTICAL': false,
            'DOOR-HANGER': false,
            'TRI-FOLD-BROCHURE': false,
            'SIGN:HORIZONTAL': false,
            'SIGN:RIDER': false,
            'SIGN:VERTICAL': false,
            'SIGN:YARD': false,
            'GREETING-CARD:HORIZONTAL': true,
            'GREETING-CARD:VERTICAL': true,
        }

        return validDirectMailProductTypeMap.hasOwnProperty(productCode) && validDirectMailProductTypeMap[productCode]
    }

    export function isValidPrintAndShipCode(productCode: Objects.Product.Code.Any | undefined): productCode is Objects.Product.Code.Fulfillment.PrintAndShip {
        if (!productCode) {
            return false
        }

        const validPrintAndShipProductTypeMap: Record<Objects.Product.Code.Any, boolean> = {
            POSTCARD: true,
            BROCHURE: true,
            FLYER: true,
            'GREETING-CARD': false,
            LETTER: false,
            'BROCHURE:PRINT-AND-SHIP-ONLY': true,
            'BUSINESS-CARD:HORIZONTAL': true,
            'BUSINESS-CARD:VERTICAL': true,
            'DOOR-HANGER': true,
            'TRI-FOLD-BROCHURE': true,
            'SIGN:HORIZONTAL': true,
            'SIGN:RIDER': true,
            'SIGN:VERTICAL': true,
            'SIGN:YARD': true,
            'GREETING-CARD:HORIZONTAL': true,
            'GREETING-CARD:VERTICAL': true,
        }

        return validPrintAndShipProductTypeMap.hasOwnProperty(productCode) && validPrintAndShipProductTypeMap[productCode]
    }

    export function isProductCode(key: string): key is Objects.Product.Code.Any {
        const map: Record<Objects.Product.Code.Any, true> = {
            BROCHURE: true,
            FLYER: true,
            'GREETING-CARD': true,
            LETTER: true,
            POSTCARD: true,
            'BROCHURE:PRINT-AND-SHIP-ONLY': true,
            'BUSINESS-CARD:HORIZONTAL': true,
            'BUSINESS-CARD:VERTICAL': true,
            'DOOR-HANGER': true,
            'TRI-FOLD-BROCHURE': true,
            'SIGN:HORIZONTAL': true,
            'SIGN:RIDER': true,
            'SIGN:VERTICAL': true,
            'SIGN:YARD': true,
            'GREETING-CARD:HORIZONTAL': true,
            'GREETING-CARD:VERTICAL': true,
        }
        return map.hasOwnProperty(key)
    }

    export function getProductCode(
        type: Objects.Product.Type.Any | undefined,
        typeVariant: Objects.Product.TypeVariant.Any | undefined
    ): Objects.Product.Code.Any | undefined {
        if (!type) {
            return undefined
        }

        if (typeVariant === undefined) {
            return type === 'BUSINESS-CARD' || type === 'SIGN' ? undefined : type
        }

        const key: Objects.Product.Code.UnSanitized = `${type}:${typeVariant}`
        return isProductCode(key) ? key : undefined
    }

    export function getProductTypeAndVariantFromCode(productCode: Objects.Product.Code.Any): {
        type: Objects.Product.Type.Any
        variant?: Objects.Product.TypeVariant.Any
    } {
        switch (productCode) {
            case 'POSTCARD':
                return { type: 'POSTCARD' }
            case 'BROCHURE':
                return { type: 'BROCHURE' }
            case 'GREETING-CARD':
                return { type: 'GREETING-CARD' }
            case 'LETTER':
                return { type: 'LETTER' }
            case 'FLYER':
                return { type: 'FLYER' }
            case 'BROCHURE:PRINT-AND-SHIP-ONLY':
                return { type: 'BROCHURE', variant: 'PRINT-AND-SHIP-ONLY' }
            case 'BUSINESS-CARD:HORIZONTAL':
                return { type: 'BUSINESS-CARD', variant: 'HORIZONTAL' }
            case 'BUSINESS-CARD:VERTICAL':
                return { type: 'BUSINESS-CARD', variant: 'VERTICAL' }
            case 'DOOR-HANGER':
                return { type: 'DOOR-HANGER' }
            case 'TRI-FOLD-BROCHURE':
                return { type: 'TRI-FOLD-BROCHURE' }
            case 'SIGN:HORIZONTAL':
                return { type: 'SIGN', variant: 'HORIZONTAL' }
            case 'SIGN:RIDER':
                return { type: 'SIGN', variant: 'RIDER' }
            case 'SIGN:VERTICAL':
                return { type: 'SIGN', variant: 'VERTICAL' }
            case 'SIGN:YARD':
                return { type: 'SIGN', variant: 'YARD' }
            case 'GREETING-CARD:HORIZONTAL':
                return { type: 'GREETING-CARD', variant: 'HORIZONTAL' }
            case 'GREETING-CARD:VERTICAL':
                return { type: 'GREETING-CARD', variant: 'VERTICAL' }
        }
        assertUnreachable(productCode)
    }

    export function getPossibleProductTypesForDocument(productType: Objects.Product.Type.Any | undefined): Objects.Product.Type.Any[] {
        let possibleProductTypes: Objects.Product.Type.Any[]
        switch (productType) {
            case 'FLYER':
                possibleProductTypes = ['FLYER', 'LETTER']
                break
            case 'LETTER':
                possibleProductTypes = ['FLYER', 'LETTER']
                break
            case 'BUSINESS-CARD':
            case 'BROCHURE':
            case 'DOOR-HANGER':
            case 'GREETING-CARD':
            case 'POSTCARD':
            case 'SIGN':
            case 'TRI-FOLD-BROCHURE':
                possibleProductTypes = [productType]
                break
            case undefined:
                possibleProductTypes = []
                break
        }
        return possibleProductTypes
    }

    export function getPossibleProductVariantsForDocument(productType: Objects.Product.Type.Any | undefined): Objects.Product.TypeVariant.Any[] {
        let possibleProductTypeVariants: Objects.Product.TypeVariant.Any[]
        switch (productType) {
            case 'FLYER':
            case 'POSTCARD':
            case 'GREETING-CARD':
            case 'TRI-FOLD-BROCHURE':
            case 'LETTER':
            case 'BUSINESS-CARD':
            case 'DOOR-HANGER':
                possibleProductTypeVariants = []
                break
            case 'SIGN':
                possibleProductTypeVariants = ['YARD', 'HORIZONTAL']
                break
            case 'BROCHURE':
                possibleProductTypeVariants = ['PRINT-AND-SHIP-ONLY']
                break
            case undefined:
                possibleProductTypeVariants = []
                break
        }
        return possibleProductTypeVariants
    }

    export function getNoPreviewImageName(
        productType: Objects.Product.Type.Any | undefined,
        productTypeVariant: Objects.Product.TypeVariant.Any | undefined
    ): string {
        const productCode = getProductCode(productType, productTypeVariant)

        if (!productCode) {
            return 'no-preview.png'
        }

        const productCodeToImageName: Record<Objects.Product.Code.Any, string> = {
            BROCHURE: 'no-preview-brochure.png',
            POSTCARD: 'no-preview-postcard.png',
            'GREETING-CARD': 'no-preview-greeting-card.png',
            LETTER: 'no-preview-letter.png',
            FLYER: 'no-preview-flyer.png',
            'BROCHURE:PRINT-AND-SHIP-ONLY': 'no-preview-brochure-print-ship.png',
            'BUSINESS-CARD:HORIZONTAL': 'no-preview-business-card-horizontal.png',
            'BUSINESS-CARD:VERTICAL': 'no-preview-business-card-vertical.png',
            'DOOR-HANGER': 'no-preview-door-hanger.png',
            'TRI-FOLD-BROCHURE': 'no-preview-tri-fold-brochure.png',
            'SIGN:HORIZONTAL': 'no-preview-sign-horizontal.png',
            'SIGN:RIDER': 'no-preview-sign-rider.png',
            'SIGN:VERTICAL': 'no-preview-sign-vertical.png',
            'SIGN:YARD': 'no-preview-sign-yard.png',
            'GREETING-CARD:HORIZONTAL': 'no-preview-greeting-card-horizontal.png',
            'GREETING-CARD:VERTICAL': 'no-preview-greeting-card-vertical.png',
        }

        const imageName = productCodeToImageName[productCode]
        return imageName
    }

    export function getNoPreviewImagePath(
        productType: Objects.Product.Type.Any | undefined,
        productTypeVariant: Objects.Product.TypeVariant.Any | undefined
    ): string {
        const imageName = getNoPreviewImageName(productType, productTypeVariant)
        return `./assets/img/no-preview/${imageName}`
    }

    export function getPageLabels(productCode: Objects.Product.Code.Any | undefined, designCount: number) {
        const sizesToProductTypesMap: Record<Objects.Product.Code.Any, string[]> = {
            'BROCHURE:PRINT-AND-SHIP-ONLY': ['Outside', 'Inside'],
            'BUSINESS-CARD:HORIZONTAL': ['Front', 'Back'],
            'BUSINESS-CARD:VERTICAL': ['Front', 'Back'],
            'DOOR-HANGER': ['Front', 'Back'],
            'GREETING-CARD': ['Front', 'Inside Top', 'Inside Bottom', 'Back'],
            'GREETING-CARD:HORIZONTAL': ['Front', 'Inside Top', 'Inside Bottom', 'Back'],
            'GREETING-CARD:VERTICAL': ['Front', 'Inside Left', 'Inside Right', 'Back'],
            'SIGN:HORIZONTAL': ['Front', 'Back'],
            'SIGN:RIDER': ['Front', 'Back'],
            'SIGN:VERTICAL': ['Front', 'Back'],
            'SIGN:YARD': ['Front', 'Back'],
            'TRI-FOLD-BROCHURE': ['Front', 'Back'],
            BROCHURE: ['Outside', 'Inside'],
            FLYER: ['Front', 'Back'],
            LETTER: ['Front', 'Back'],
            POSTCARD: ['Front', 'Back'],
        }

        const defaultLabels = ['Front', 'Back']
        const selectedLabels = productCode ? sizesToProductTypesMap[productCode] : defaultLabels

        return selectedLabels.slice(0, designCount)
    }

    export function isDisabledProductCode(
        productCode: Objects.Product.Code.Any
    ): productCode is Extract<Objects.Product.Code.Any, Objects.Product.Type.GreetingCard> {
        switch (productCode) {
            case 'GREETING-CARD':
                return true
            case 'BROCHURE':
            case 'FLYER':
            case 'LETTER':
            case 'POSTCARD':
            case 'BROCHURE:PRINT-AND-SHIP-ONLY':
            case 'BUSINESS-CARD:VERTICAL':
            case 'BUSINESS-CARD:HORIZONTAL':
            case 'DOOR-HANGER':
            case 'TRI-FOLD-BROCHURE':
            case 'SIGN:RIDER':
            case 'SIGN:YARD':
            case 'SIGN:VERTICAL':
            case 'SIGN:HORIZONTAL':
            case 'GREETING-CARD:HORIZONTAL':
            case 'GREETING-CARD:VERTICAL':
                return false
        }

        assertUnreachable(productCode)
        return false
    }

    export function getQueryParameterFromCode(productCode: Objects.Product.Code.Any): Objects.Product.QueryParameter.Any {
        switch (productCode) {
            case 'BROCHURE':
                return '11x17 Brochure'
            case 'BROCHURE:PRINT-AND-SHIP-ONLY':
                return '11X17 Print Only Brochure'
            case 'BUSINESS-CARD:HORIZONTAL':
                return '2x3.5 Business Card'
            case 'BUSINESS-CARD:VERTICAL':
                return '3.5x2 Business Card'
            case 'DOOR-HANGER':
                return '11x4.25 Door Hanger'
            case 'FLYER':
                return '8.5x11 Flyer'
            case 'GREETING-CARD':
                return '5x7 Greeting Card'
            case 'GREETING-CARD:HORIZONTAL':
                return '5.5x8.5 Greeting Card'
            case 'GREETING-CARD:VERTICAL':
                return '8.5x5.5 Greeting Card'
            case 'LETTER':
                return '8.5x11 Letter'
            case 'POSTCARD':
                return '6x9 Postcard'
            case 'SIGN:HORIZONTAL':
                return '18x24 Sign Horizontal'
            case 'SIGN:RIDER':
                return '6x24 Sign Rider'
            case 'SIGN:VERTICAL':
                return '36x24 Sign Vertical'
            case 'SIGN:YARD':
                return '18x24 Sign Yard'
            case 'TRI-FOLD-BROCHURE':
                return '8.5x11 Tri-Fold Brochure'
        }

        assertUnreachable(productCode)
    }

    export function getCodeFromProductQueryParameter(productQueryParameter: string): Objects.Product.Code.Any | undefined {
        const allProductQueryParameters: Objects.Product.QueryParameter.All = [
            '11x17 Brochure',
            '11X17 Print Only Brochure',
            '8.5x11 Tri-Fold Brochure',
            '2x3.5 Business Card',
            '3.5x2 Business Card',
            '11x4.25 Door Hanger',
            '5x7 Greeting Card',
            '5.5x8.5 Greeting Card',
            '8.5x5.5 Greeting Card',
            '8.5x11 Flyer',
            '8.5x11 Letter',
            '6x9 Postcard',
            '18x24 Sign Horizontal',
            '6x24 Sign Rider',
            '36x24 Sign Vertical',
            '18x24 Sign Yard',
        ]

        const unstrictAllProductQueryParameters: string[] = allProductQueryParameters

        const sanitizedProductQueryParameter = allProductQueryParameters.find((v): v is Objects.Product.QueryParameter.Any => {
            return v === productQueryParameter && unstrictAllProductQueryParameters.includes(productQueryParameter)
        })

        switch (sanitizedProductQueryParameter) {
            case undefined:
                return
            case '11X17 Print Only Brochure':
                return 'BROCHURE:PRINT-AND-SHIP-ONLY'
            case '11x17 Brochure':
                return 'BROCHURE'
            case '11x4.25 Door Hanger':
                return 'DOOR-HANGER'
            case '18x24 Sign Horizontal':
                return 'SIGN:HORIZONTAL'
            case '18x24 Sign Yard':
                return 'SIGN:YARD'
            case '2x3.5 Business Card':
                return 'BUSINESS-CARD:HORIZONTAL'
            case '3.5x2 Business Card':
                return 'BUSINESS-CARD:VERTICAL'
            case '36x24 Sign Vertical':
                return 'SIGN:VERTICAL'
            case '5.5x8.5 Greeting Card':
                return 'GREETING-CARD:HORIZONTAL'
            case '5x7 Greeting Card':
                return 'GREETING-CARD'
            case '6x24 Sign Rider':
                return 'SIGN:RIDER'
            case '6x9 Postcard':
                return 'POSTCARD'
            case '8.5x11 Flyer':
                return 'FLYER'
            case '8.5x11 Letter':
                return 'LETTER'
            case '8.5x11 Tri-Fold Brochure':
                return 'TRI-FOLD-BROCHURE'
            case '8.5x5.5 Greeting Card':
                return 'GREETING-CARD:VERTICAL'
        }

        assertUnreachable(sanitizedProductQueryParameter)
        return undefined
    }

    // TODO(WSP-1130) Rename
    export function getProductTypeLabel(productCode: Objects.Product.Code.Any): string {
        switch (productCode) {
            case 'BROCHURE':
            case 'BROCHURE:PRINT-AND-SHIP-ONLY':
            case 'TRI-FOLD-BROCHURE':
                return 'Brochures'
            case 'BUSINESS-CARD:HORIZONTAL':
            case 'BUSINESS-CARD:VERTICAL':
                return 'Business Cards'
            case 'DOOR-HANGER':
                return 'Door Hangers'
            case 'FLYER':
                return 'Flyers'
            case 'GREETING-CARD':
            case 'GREETING-CARD:HORIZONTAL':
            case 'GREETING-CARD:VERTICAL':
                return 'Greeting Cards'
            case 'LETTER':
                return 'Letters'
            case 'POSTCARD':
                return 'Postcards'
            case 'SIGN:HORIZONTAL':
            case 'SIGN:RIDER':
            case 'SIGN:VERTICAL':
            case 'SIGN:YARD':
                return 'Signs'
        }

        assertUnreachable(productCode)
        return ''
    }
    export function getDesignServiceCodeFromProductCode(productCode: Objects.Product.Code.Any): Objects.DesignServiceProduct.Code {
        switch (productCode) {
            case 'POSTCARD':
                return 'CUSTOM POSTCARD'
            case 'BROCHURE':
            case 'BROCHURE:PRINT-AND-SHIP-ONLY':
                return 'CUSTOM BROCHURE'
            case 'GREETING-CARD':
                return 'CUSTOM GREETING CARD'
            case 'LETTER':
                return 'CUSTOM LETTER'
            case 'FLYER':
                return 'CUSTOM FLYER'
            case 'BUSINESS-CARD:VERTICAL':
                return 'CUSTOM BUSINESS CARD'
            case 'BUSINESS-CARD:HORIZONTAL':
                return 'CUSTOM BUSINESS CARD'
            case 'DOOR-HANGER':
                return 'CUSTOM DOOR HANGER'
            case 'TRI-FOLD-BROCHURE':
                return 'CUSTOM TRI FOLD BROCHURE'
            case 'SIGN:RIDER':
                return 'CUSTOM SIGN'
            case 'SIGN:YARD':
                return 'CUSTOM SIGN'
            case 'SIGN:VERTICAL':
                return 'CUSTOM SIGN'
            case 'SIGN:HORIZONTAL':
                return 'CUSTOM SIGN'
            case 'GREETING-CARD:HORIZONTAL':
                return 'CUSTOM GREETING CARD'
            case 'GREETING-CARD:VERTICAL':
                return 'CUSTOM GREETING CARD'
        }
        assertUnreachable(productCode)
    }
}
